import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocation } from "react-router-dom"
import { Check, Loader2, MessageSquare, MessageSquarePlus, Undo2 } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  crearComentario,
  fetchComentarios,
  fetchConteo,
  reabrirComentario,
  resolverComentario,
} from "@/routes/comentarios/api"
import type { AmbitoComentario, Comentario } from "@/routes/comentarios/api"

/**
 * COMENTAR LO QUE NO CUADRA, DONDE SE VE.
 *
 * Cesar: «es importante poder dejar comentarios al encargado de cosas que no
 * nos cuadren», sobre las cuatro cosas: una cifra de un reporte, un asiento o
 * comprobante, una factura por cobrar, y un hilo general por sociedad y período.
 *
 * ===========================================================================
 * UN SOLO BOTÓN PARA LAS CUATRO
 * ===========================================================================
 *
 * Lo que cambia entre los cuatro sitios es el par `(ambito, referencia)`: el
 * ancla. Todo lo demás —escribir, ver el hilo, cerrar diciendo qué se hizo— es
 * idéntico, así que es un componente y no cuatro. Cuatro componentes parecidos
 * divergen en la primera semana y nadie se entera hasta que uno de los cuatro
 * sitios deja de poder cerrar un comentario.
 *
 * ===========================================================================
 * CIENTO OCHENTA CELDAS, UNA PETICIÓN
 * ===========================================================================
 *
 * El P&L tiene doce columnas por quince filas. Si cada botón preguntara «¿hay
 * algo dicho sobre mí?», pintar la tabla costaría ciento ochenta peticiones.
 *
 * No hace falta un proveedor de contexto ni pasar el conteo por props hasta la
 * última celda: **todos los botones de una pantalla usan la MISMA `queryKey`**
 * —cliente, sociedad, año— y React Query deduplica las consultas idénticas. Se
 * monta una vez, se pide una vez, y cada botón lee del mismo caché. Y al dejar
 * un comentario se invalida esa clave: las ciento ochenta marcas se actualizan
 * con una sola respuesta.
 *
 * ===========================================================================
 * LA MARCA SÓLO CUENTA LO ABIERTO
 * ===========================================================================
 *
 * Un comentario resuelto ya no es algo que mirar. Si contara, la marca no se
 * apagaría nunca y dejaría de significar «aquí hay algo» para significar
 * «alguna vez hubo algo». El hilo completo sigue estando: se ve al abrir.
 */

export interface AnclaComentario {
  ambito: AmbitoComentario
  /** Vacía sólo en `general`. Construir con los `ref*` de `routes/comentarios/api`. */
  referencia: string
  /** De qué habla, en palabras: «Ingresos · agosto». Lo lee la bandeja. */
  rotulo: string
  anio?: number | null
  mes?: number | null
}

/** La clave compartida por TODOS los botones de una pantalla. Ver la cabecera. */
function claveDelConteo(tenantId: string | null, entityId: string | null, anio?: number | null) {
  return ["comentarios-conteo", tenantId, entityId, anio ?? null] as const
}

/**
 * El botón que se pone al lado de una cifra, de una fila o de una factura.
 *
 * DISCRETO CUANDO NO HAY NADA, VISIBLE CUANDO SÍ. Sin comentarios es un icono
 * apagado que sólo se nota al pasar por encima: una tabla con ciento ochenta
 * iconos a pleno contraste no es una tabla, es un formulario. Con comentarios
 * abiertos lleva el número y el color de aviso.
 */
export function BotonComentario({
  ancla,
  className,
  /** `icono` para una celda; `texto` para una cabecera o una barra. */
  variante = "icono",
}: {
  ancla: AnclaComentario
  className?: string
  variante?: "icono" | "texto"
}) {
  const { activeTenantId, activeEntityId } = useAuth()
  const [abierto, setAbierto] = React.useState(false)

  const conteo = useQuery({
    queryKey: claveDelConteo(activeTenantId, activeEntityId, ancla.anio),
    queryFn: () => fetchConteo({ entityId: activeEntityId, anio: ancla.anio ?? undefined }),
    enabled: Boolean(activeTenantId) && Boolean(activeEntityId),
  })

  const abiertos = conteo.data?.por_referencia[ancla.referencia] ?? 0

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        title={
          abiertos > 0
            ? `${abiertos} comentario(s) sin resolver sobre «${ancla.rotulo}»`
            : `Comentar «${ancla.rotulo}»`
        }
        aria-label={abiertos > 0 ? `Ver comentarios de ${ancla.rotulo}` : `Comentar ${ancla.rotulo}`}
        className={cn(
          /*
            EL DISEÑO DEL MARCADOR. Cesar, 2026-10-05: «el diseño sigue sin verse
            muy bien en algunas partes». Dos formas y nada más:

            - `icono`: un cuadrado de 24 px, alineado con el texto de la fila,
              con el icono a 14 px en gris texto; al pasar el cursor, borde y
              fondo como cualquier botón fantasma. Con comentarios abiertos
              se vuelve una PÍLDORA ámbar con el número: se ve barriendo la
              tabla, que es para lo que existe.
            - `texto`: un botón de contorno como los demás de la cabecera
              («Comentar esta vista» / «3 comentarios»), para no inventar un
              tercer tipo de control.
          */
          "inline-flex shrink-0 items-center justify-center gap-1 rounded-md text-2xs",
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          variante === "icono"
            ? (abiertos > 0
                ? "h-6 min-w-6 px-1.5 bg-warning-soft font-medium text-warning-foreground hover:bg-warning-soft/70"
                : "h-6 w-6 text-muted-foreground hover:border hover:border-border-strong hover:bg-accent hover:text-foreground")
            : (abiertos > 0
                ? "h-7 border border-warning/50 bg-warning-soft px-2 font-medium text-warning-foreground hover:bg-warning-soft/70"
                : "h-7 border bg-surface px-2 text-muted-foreground shadow-xs hover:border-border-strong hover:text-foreground"),
          className,
        )}
      >
        {abiertos > 0 ? (
          <>
            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="num">{abiertos}</span>
          </>
        ) : (
          <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {variante === "texto" ? (
          <span>{abiertos > 0 ? (abiertos === 1 ? t("comentario") : t("comentarios")) : t("Comentar")}</span>
        ) : null}
      </button>

      {/* El hilo se monta sólo al abrirlo: montarlo cerrado en cada celda
          dispararía una consulta del hilo por celda, que es justo lo que el
          conteo por lote existe para evitar. */}
      {abierto ? (
        <DialogoDelHilo ancla={ancla} abierto={abierto} onOpenChange={setAbierto} />
      ) : null}
    </>
  )
}

function DialogoDelHilo({
  ancla,
  abierto,
  onOpenChange,
}: {
  ancla: AnclaComentario
  abierto: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { activeTenantId, activeEntityId } = useAuth()
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const [texto, setTexto] = React.useState("")

  const hilo = useQuery({
    queryKey: ["comentarios", activeTenantId, activeEntityId, ancla.ambito, ancla.referencia],
    queryFn: () =>
      fetchComentarios({
        entityId: activeEntityId,
        ambito: ancla.ambito,
        referencia: ancla.referencia,
        // `todos`: el hilo enseña también lo resuelto. La MARCA sólo cuenta lo
        // abierto, pero al abrir hay que poder leer qué se contestó ya — si no,
        // la misma observación se vuelve a dejar cada mes.
        estado: "todos",
      }),
    enabled: Boolean(activeEntityId),
  })

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["comentarios"] })
    void qc.invalidateQueries({ queryKey: ["comentarios-conteo"] })
  }

  const dejar = useMutation({
    mutationFn: () =>
      crearComentario({
        entity_id: activeEntityId as string,
        ambito: ancla.ambito,
        referencia: ancla.referencia,
        rotulo: ancla.rotulo,
        anio: ancla.anio ?? null,
        mes: ancla.mes ?? null,
        texto: texto.trim(),
      }),
    onSuccess: () => {
      toast({ title: "Anotado", description: `Sobre «${ancla.rotulo}».` })
      setTexto("")
      refrescar()
    },
    onError: onWriteError("el comentario", "dejar"),
  })

  return (
    <Dialog open={abierto} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{ancla.rotulo || t("Comentarios")}</DialogTitle>
          <DialogDescription>
            {t("Lo que anotes aquí queda con tu nombre y con la fecha, y no se cierra hasta que alguien diga qué hizo.")}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[22rem] space-y-2 overflow-y-auto">
          {hilo.isLoading ? (
            <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("Buscando lo que ya se dijo…")}
            </p>
          ) : (hilo.data?.comentarios.length ?? 0) === 0 ? (
            <p className="text-2xs leading-5 text-muted-foreground">
              {t("Todavía no hay nada dicho sobre esto. Sé el primero.")}
            </p>
          ) : (
            hilo.data?.comentarios.map((c) => (
              <Ficha key={c.id} c={c} puedeCerrar={can("edit")} onCambio={refrescar} />
            ))
          )}
        </div>

        <div className="space-y-2 border-t pt-3">
          {/*
            LOS COMENTARIOS VAN SOBRE UNA SOCIEDAD (es lo que los aísla y lo que
            la bandeja agrupa). En el consolidado no hay una: se dice, en vez
            de dejar un botón que no hace nada.
          */}
          {!activeEntityId ? (
            <p className="text-2xs leading-4 text-muted-foreground">
              {t("Los comentarios van sobre una sociedad. Elige una arriba para dejar el tuyo.")}
            </p>
          ) : null}
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={3}
            disabled={!activeEntityId}
            placeholder={t("Qué no te cuadra, y con qué lo comparaste.")}
            className="text-xs"
          />
          <div className="flex items-center justify-end gap-2">
            {/*
              SIN EXIGIR `edit`. Es el punto de la función: quien MIRA tiene que
              poder señalarle algo a quien opera. Con permiso de edición sólo
              podrían comentar los que ya pueden arreglarlo ellos mismos.
            */}
            <Button
              size="sm"
              disabled={!activeEntityId || texto.trim().length < 3 || dejar.isPending}
              onClick={() => dejar.mutate()}
            >
              {dejar.isPending ? <Loader2 className="animate-spin" /> : <MessageSquarePlus />}
              {dejar.isPending ? "Anotando…" : "Dejar el comentario"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Un comentario del hilo, con lo que se hizo si ya se cerró. */
function Ficha({
  c,
  puedeCerrar,
  onCambio,
}: {
  c: Comentario
  puedeCerrar: boolean
  onCambio: () => void
}) {
  const { toast } = useToast()
  const [respuesta, setRespuesta] = React.useState("")
  const [cerrando, setCerrando] = React.useState(false)

  const resolver = useMutation({
    mutationFn: () => resolverComentario(c.id, respuesta.trim()),
    onSuccess: () => {
      toast({ title: "Comentario resuelto" })
      setCerrando(false)
      setRespuesta("")
      onCambio()
    },
    onError: onWriteError("el comentario", "resolver"),
  })

  const reabrir = useMutation({
    mutationFn: () => reabrirComentario(c.id),
    onSuccess: () => {
      toast({ title: "Vuelve a estar abierto" })
      onCambio()
    },
    onError: onWriteError("el comentario", "reabrir"),
  })

  const resuelto = c.estado === "resuelto"

  return (
    <div
      className={cn(
        "rounded-md border px-2.5 py-2",
        resuelto ? "bg-surface-sunken/40 opacity-80" : "bg-surface",
      )}
    >
      <p className="text-xs leading-5">{c.texto}</p>
      <p className="mt-0.5 text-2xs text-muted-foreground">
        {c.autor ?? c.autor_email ?? "Alguien"} · {new Date(c.creado_en).toLocaleDateString()}
      </p>

      {resuelto ? (
        /*
          LA RESPUESTA SE QUEDA A LA VISTA aunque el comentario esté cerrado, y
          con el filete verde a la izquierda. Es lo que evita que la misma
          observación se vuelva a dejar el mes siguiente: quien llega ve que ya
          se preguntó Y qué se contestó.
        */
        <div className="mt-1.5 border-l-2 border-success/50 pl-2">
          <p className="text-2xs leading-5">
            <span className="font-medium text-success">{t("Resuelto:")}{" "}</span>
            {c.respuesta}
          </p>
          <p className="mt-px text-2xs text-muted-foreground">
            {c.resuelto_por ?? "Alguien"}
            {c.resuelto_en ? ` · ${new Date(c.resuelto_en).toLocaleDateString()}` : ""}
          </p>
          {puedeCerrar ? (
            <Button
              size="xs"
              variant="ghost"
              className="mt-1"
              disabled={reabrir.isPending}
              onClick={() => reabrir.mutate()}
            >
              <Undo2 />
              {t("Reabrir")}
            </Button>
          ) : null}
        </div>
      ) : puedeCerrar ? (
        cerrando ? (
          <div className="mt-1.5 space-y-1.5">
            <Textarea
              value={respuesta}
              onChange={(e) => setRespuesta(e.target.value)}
              rows={2}
              placeholder={t("Qué hiciste. Sin esto no se cierra.")}
              className="text-xs"
            />
            <div className="flex gap-1.5">
              <Button
                size="xs"
                disabled={respuesta.trim().length < 3 || resolver.isPending}
                onClick={() => resolver.mutate()}
              >
                {resolver.isPending ? <Loader2 className="animate-spin" /> : <Check />}
                {t("Resolver")}
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setCerrando(false)}>
                {t("Cancelar")}
              </Button>
            </div>
          </div>
        ) : (
          <Button size="xs" variant="outline" className="mt-1.5" onClick={() => setCerrando(true)}>
            <Check />
            {t("Marcar como resuelto")}
          </Button>
        )
      ) : null}
    </div>
  )
}


/**
 * COMENTAR ESTA VISTA. Cesar, 2026-10-05: «debería de poder dejar comentarios
 * para el equipo en todas las vistas de finanzas por lo menos y seguro en
 * algunas de contabilidad». Un botón en la barra de pestañas, el mismo en
 * todas: el hilo se ancla a la RUTA de la vista («vista:/finanzas/caja»), así
 * que quien abra Flujo de caja la semana que viene encuentra lo que se dijo
 * sobre Flujo de caja, y la bandeja de Pendientes enlaza de vuelta.
 *
 * Las celdas que importan siguen teniendo su propio marcador (una cuenta del
 * P&L, una factura, un comprobante): esto es para lo que no cabe en una celda.
 */
export function ComentariosDeLaVista({
  seccion, vistas, anio,
}: {
  seccion: string
  vistas: Array<{ to: string; label: string }>
  anio?: number | null
}) {
  const { pathname } = useLocation()
  const vista = vistas.find((v) => pathname.split("/").includes(v.to))
  const rotulo = `${seccion} › ${vista?.label ?? pathname}${anio ? ` · ${anio}` : ""}`
  return (
    <BotonComentario
      variante="texto"
      ancla={{ ambito: "general", referencia: `vista:${pathname}`, rotulo, anio: anio ?? null }}
    />
  )
}

/** De una referencia «vista:/ruta» a la ruta, para enlazar de vuelta desde la bandeja. */
export function rutaDeLaReferencia(referencia: string): string | null {
  return referencia.startsWith("vista:/") ? referencia.slice("vista:".length) : null
}
